/**
 * FunnelDrilldown — Radix Dialog showing prospects at a funnel stage.
 * Opens when a bar is clicked in FunnelChart.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useDrilldown } from '@/lib/api/hooks/useAnalytics';
import type { DrilldownProspect, FunnelParams } from '@/types/analytics';

interface FunnelDrilldownProps {
  stage: string | null;
  onClose: () => void;
  dateFrom?: string;
  dateTo?: string;
  campaignId?: string;
  source?: string;
}

const columnHelper = createColumnHelper<DrilldownProspect>();

const STAGE_LABELS: Record<string, string> = {
  lead: 'Lead',
  scheduled: 'Scheduled',
  showed: 'Showed',
  qualified: 'Qualified',
  proposal: 'Proposal',
  closed_won: 'Closed Won',
};

const PAGE_SIZE = 50;

export function FunnelDrilldown({
  stage,
  onClose,
  dateFrom,
  dateTo,
  campaignId,
  source,
}: FunnelDrilldownProps) {
  const [offset, setOffset] = useState(0);

  // Reset pagination when the stage changes
  useEffect(() => { setOffset(0); }, [stage]);

  const params: FunnelParams & { limit?: number; offset?: number } = {
    date_from: dateFrom,
    date_to: dateTo,
    campaign_id: campaignId,
    source,
    limit: PAGE_SIZE,
    offset,
  };

  const { data, isLoading, error } = useDrilldown(stage, params);

  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: info => info.getValue() || <span className="text-muted-foreground">—</span>,
    }),
    columnHelper.accessor('company_name', {
      header: 'Company',
      cell: info => info.getValue() || <span className="text-muted-foreground">—</span>,
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: info => (
        <Badge variant="outline" className="text-xs capitalize">
          {info.getValue()?.replace(/_/g, ' ')}
        </Badge>
      ),
    }),
    columnHelper.accessor('days_in_stage', {
      header: 'Days in Stage',
      cell: info => {
        const v = info.getValue();
        return v !== null && v !== undefined ? v : <span className="text-muted-foreground">—</span>;
      },
    }),
    columnHelper.accessor('call_count', {
      header: 'Calls',
    }),
    columnHelper.accessor('emails_sent', {
      header: 'Emails',
    }),
  ];

  const table = useReactTable({
    data: data?.prospects ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const stageLabel = stage ? (STAGE_LABELS[stage] ?? stage) : '';

  return (
    <Dialog open={!!stage} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{stageLabel} Prospects</DialogTitle>
        </DialogHeader>

        {/* Aggregate metrics */}
        {data && (
          <div className="grid grid-cols-3 gap-3 py-2 border-b">
            <div className="text-center">
              <p className="text-xl font-bold">{data.metrics.avg_days_in_stage}</p>
              <p className="text-xs text-muted-foreground">Avg Days in Stage</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold">
                {data.metrics.avg_call_count}
              </p>
              <p className="text-xs text-muted-foreground">Avg Calls</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold">
                {data.metrics.conversion_rate_to_next !== null
                  ? `${data.metrics.conversion_rate_to_next}%`
                  : '—'}
              </p>
              <p className="text-xs text-muted-foreground">Conversion to Next</p>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : error ? (
            <p className="text-center text-destructive py-8">
              Failed to load prospects: {(error as Error).message}
            </p>
          ) : data?.prospects.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No prospects at this stage.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background border-b">
                {table.getHeaderGroups().map(hg => (
                  <tr key={hg.id}>
                    {hg.headers.map(h => (
                      <th
                        key={h.id}
                        className="px-3 py-2 text-left font-medium text-muted-foreground"
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="border-b hover:bg-muted/30">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-3 py-2">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {data && data.total > PAGE_SIZE && (
          <div className="flex items-center justify-between pt-2 border-t text-sm text-muted-foreground">
            <span>
              {data.total} total · page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + PAGE_SIZE >= data.total}
                onClick={() => setOffset(o => o + PAGE_SIZE)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
